/**
 * M11 — circles. One of: unread messages in a circle, an event within 3
 * days with an inline RSVP, or (no memberships) one suggested public circle.
 * Row, class 3. The tap is `open`; the host's route map picks the chat, the
 * event or the circle by variant. The inline RSVP is the `rsvp` verb; when a
 * host provides HomeInlineContext the control shows the server's answer.
 */

import { Users } from 'react-feather';
import { HomeAction, HomeGlyph, HomeRow } from '../HomeKit';
import { useHomeInline } from '../inlineState';
import type { ModuleProps } from '../moduleTypes';

export function CirclesModule({ module, onAction }: ModuleProps) {
  const hosted = useHomeInline();
  if (module.type !== 'circles') return null;
  const { payload } = module;
  const act = (a: Parameters<NonNullable<typeof onAction>>[0]) => onAction?.(a, module);
  const dismiss = module.dismissable ? () => act({ kind: 'dismiss', dismissKey: module.dismissKey }) : undefined;

  let body: string;
  let sub: string | undefined;
  let trailing: React.ReactNode = null;

  if (payload.variant === 'event' && payload.event) {
    const ev = payload.event;
    const rsvp = hosted ? hosted.rsvp(ev.id) : null;
    body = `${ev.title} · ${ev.goingCount} going`;
    sub = rsvp === 'going' ? 'you’re going' : 'rsvp from here';
    trailing =
      rsvp === 'going' ? (
        <span className="home-status home-status--ready">going</span>
      ) : (
        <HomeAction small disabled={rsvp === 'pending'} onClick={() => act({ kind: 'rsvp', circleId: payload.circleId, eventId: ev.id })}>
          rsvp
        </HomeAction>
      );
  } else if (payload.variant === 'suggest') {
    body = payload.circleName;
    sub = 'a circle you could join';
  } else {
    body = `${payload.unread} new in ${payload.circleName}`;
  }

  return (
    <HomeRow
      reason={module.reason}
      body={body}
      sub={sub}
      glyph={
        <HomeGlyph tone="violet">
          <Users size={14} />
        </HomeGlyph>
      }
      trailing={trailing}
      dismissable={module.dismissable}
      onDismiss={dismiss}
      onOpen={() => act({ kind: 'open' })}
    />
  );
}
